import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './SurveyBuilderView.css';
import type { SurveyBuilderViewProps, SurveyForm } from './types';
import { BUILDER_STEPS } from './data';
import { useSurveyBuilder } from './hooks/useSurveyBuilder';
import { useCreateSurvey } from './hooks/useCreateSurvey';
import { useSaveAsTemplate, useUpdateSurvey, useCreateTemplate } from '@/hooks/useSurveys';
import { BuilderContext } from './context/BuilderContext';
import { IconPickerModal } from './components/IconPickerModal';
import { PreviewPanel } from './components/PreviewPanel';
import { StepInfo } from './steps/StepInfo';
import { StepAudiencia } from './steps/StepAudiencia';
import { StepPreguntas } from './steps/StepPreguntas';
import { StepConfig } from './steps/StepConfig';
import { StepPublicar } from './steps/StepPublicar';

function deriveOuIds(audienceOUs: string[], audienceSubOUs: Record<string, string[]>): string[] {
  if (audienceOUs.length === 0) return ['global'];
  const ids = new Set<string>();
  for (const ouKey of audienceOUs) {
    ids.add(ouKey);
    const sub = audienceSubOUs[ouKey];
    if (sub && sub.length > 0) sub.forEach(s => ids.add(s));
  }
  return [...ids];
}

function buildMetaTranslations(form: SurveyForm, key: string): Record<string, string> | undefined {
  const result: Record<string, string> = {};
  for (const [lang, map] of Object.entries(form.translations)) {
    if (form.translationsEnabled[lang] === false) continue;
    const v = map[key];
    if (v?.trim()) result[lang] = v;
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

function toUpdateAudienceContract(form: SurveyForm) {
  if (form.surveyType === 'personalizada') {
    switch (form.destMode) {
      case 'meeting': return { audienceType: 'meeting' as const, ouIds: [], targetMeetingId: form.sessionId || undefined };
      case 'people':  return { audienceType: 'people' as const, ouIds: [], targetUserIds: form.destPeople };
      case 'ou':      return { audienceType: 'ou' as const, ouIds: form.destOU ? [form.destOU] : [] };
    }
  }
  return {
    audienceType: 'ou' as const,
    ouIds: deriveOuIds(form.audienceOUs, form.audienceSubOUs),
  };
}


export function SurveyBuilderView({ mode, surveyId, template, source, onClose }: SurveyBuilderViewProps) {
  const { t } = useTranslation();
  const [saveTemplateMsg, setSaveTemplateMsg] = useState<string | null>(null);
  const [publishError, setPublishError] = useState<string | null>(null);
  const createSurvey = useCreateSurvey();
  const updateSurvey = useUpdateSurvey();
  const saveTemplate = useSaveAsTemplate();
  const createTemplate = useCreateTemplate();
  const saveTemplateMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (saveTemplateMsgTimerRef.current !== null) {
        clearTimeout(saveTemplateMsgTimerRef.current);
      }
    };
  }, []);

  const showTemplateSavedMsg = () => {
    setSaveTemplateMsg(t('admin.builderSaveTemplateMock'));
    if (saveTemplateMsgTimerRef.current !== null) {
      clearTimeout(saveTemplateMsgTimerRef.current);
    }
    saveTemplateMsgTimerRef.current = setTimeout(() => {
      setSaveTemplateMsg(null);
      saveTemplateMsgTimerRef.current = null;
    }, 3000);
  };

  const handleSaveTemplate = () => {
    const existingId = surveyId ?? template?.id;
    const onSaveSuccess = source === 'templates' ? () => onClose() : showTemplateSavedMsg;

    if (existingId != null) {
      // Encuesta/template ya existente — copiar como template
      saveTemplate.mutate(String(existingId), { onSuccess: onSaveSuccess });
    } else {
      // Sin ID previo — crear template directamente sin survey intermedio
      createTemplate.mutate(form, {
        onSuccess: onSaveSuccess,
        onError: (err) => setPublishError(err.message),
      });
    }
  };

  const {
    step, setStep,
    form, setForm,
    showExitModal,
    validationErrors,
    previewIconTarget, setPreviewIconTarget,
    enfoqueFilter, setEnfoqueFilter,
    previewVisible, setPreviewVisible,
    previewWidth,
    handleRef,
    isLast,
    goNext, goPrev,
    handleClose, handleExitCancel, handleExitConfirm,
    clearValidationErrors,
    onHandleMouseDown,
  } = useSurveyBuilder(mode, onClose, template, surveyId);

  return (
    <BuilderContext.Provider value={{ form, setForm, enfoqueFilter, setEnfoqueFilter, source }}>
    <div className="survey-builder-view">

      {/* ── Topbar ── */}
      <header className="bfp-topbar">
        <div className="bfp-breadcrumb">
          <button className="bfp-back-btn" onClick={handleClose}>
            <i className="fas fa-arrow-left" />
            <span>{t('admin.builderBackToSurveys')}</span>
          </button>
          <i className="fas fa-chevron-right bfp-sep-icon" />
          <span className="bfp-current">
            {source === 'templates'
              ? (mode === 'edit' ? t('admin.builderEditTemplateTitle') : t('admin.builderNewTemplateTitle'))
              : (mode === 'edit' ? t('admin.builderEditTitle') : t('admin.builderNewTitle'))}
          </span>
        </div>
        <div className="bfp-topbar-right">
          <button className="bfp-close-btn" onClick={handleClose} title={t('admin.builderCloseEditor')}>
            <i className="fas fa-times" />
          </button>
        </div>
      </header>

      {/* ── Steps navigation ── */}
      <div className="bld-steps-nav">
        {BUILDER_STEPS.map((s, i) => {
          const isLastStep = i === BUILDER_STEPS.length - 1;
          const stepIcon = source === 'templates' && isLastStep ? 'fas fa-save' : s.icon;
          const stepTKey = source === 'templates' && isLastStep ? 'admin.builderNavGuardar' : s.tKey;
          return (
          <div key={s.id} style={{ display: 'contents' }}>
            <button
              className={`bld-step-btn${i === step ? ' active' : ''}${i < step ? ' done' : ''}`}
              onClick={() => { if (i < step) setStep(i as typeof step); }}
              style={{ cursor: i < step ? 'pointer' : i === step ? 'default' : 'not-allowed' }}
            >
              <span className="bld-step-num">
                {i < step ? <i className="fas fa-check" /> : i + 1}
              </span>
              <i className={stepIcon} />
              <span>{t(stepTKey as Parameters<typeof t>[0])}</span>
            </button>
            {i < BUILDER_STEPS.length - 1 && (
              <div className={`bld-step-connector${i < step ? ' done' : ''}`} />
            )}
          </div>
        );
        })}
      </div>

      {/* ── Body ── */}
      <div className="bld-body">

        {/* Form panel */}
        <div className="bld-form-panel">
          <div className="bld-form-body">
            {step === 0 && <StepInfo />}
            {step === 1 && <StepAudiencia />}
            {step === 2 && <StepPreguntas />}
            {step === 3 && <StepConfig />}
            {step === 4 && <StepPublicar />}
          </div>

          {/* Publish error */}
          {publishError !== null && (
            <div className="bld-publish-error" role="alert">
              <i className="fas fa-exclamation-circle" /> {publishError}
            </div>
          )}

          {/* Bottom actions */}
          <div className="bld-step-actions">
            <button className="bld-btn-prev" onClick={goPrev} disabled={step === 0}>
              <i className="fas fa-arrow-left" /> {t('admin.builderPrevious')}
            </button>
            <div className="bld-step-actions-right">
              {/* Create mode: save-as-template notice + button */}
              {isLast && mode === 'create' && (
                <>
                  {saveTemplateMsg !== null && (
                    <div className="bld-save-template-notice" role="status">
                      <i className="fas fa-check-circle" /> {saveTemplateMsg}
                      <button
                        type="button"
                        className="bld-save-template-notice-dismiss"
                        onClick={() => setSaveTemplateMsg(null)}
                        aria-label={t('common.close')}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  )}
                  <button className="bld-btn-save-template" onClick={handleSaveTemplate}>
                    <i className="fas fa-layer-group" /> {t('admin.builderSaveTemplate')}
                  </button>
                </>
              )}

              {!isLast ? (
                <button className="bld-btn-next" onClick={goNext}>
                  {t('admin.builderNext')} <i className="fas fa-arrow-right" />
                </button>
              ) : mode === 'edit' ? (
                /* Edit mode: Cancel + Save */
                <>
                  <button className="bld-btn-prev" onClick={onClose} style={{ marginLeft: 0 }}>
                    <i className="fas fa-times" /> {t('common.cancel')}
                  </button>
                  <button
                    className="bld-btn-next"
                    disabled={updateSurvey.isPending}
                    onClick={() => {
                      const effectiveId = surveyId ?? template?.id;
                      if (!effectiveId) return;
                      setPublishError(null);
                      updateSurvey.mutate({
                        id: effectiveId,
                        body: {
                          name: form.name,
                          description: form.description || undefined,
                          category: form.surveyType || undefined,
                          ...toUpdateAudienceContract(form),
                          nameTranslations: buildMetaTranslations(form, 'survey_name'),
                          descriptionTranslations: buildMetaTranslations(form, 'survey_desc'),
                          startDate: form.startDate ? `${form.startDate}T00:00:00Z` : undefined,
                          expiresAt: form.expirationDate ? `${form.expirationDate}T00:00:00Z` : undefined,
                          questions: form.questions.map((q, idx) => ({
                            order: idx + 1,
                            questionType: q.type === 'nps' ? 'nps' : q.type === 'open' ? 'open' : 'behavior',
                            dailyHabitId: q.coreBehavior || undefined,
                            fundamentalId: q.fundamental || undefined,
                            focusType: q.enfoque || undefined,
                            presentationMode: q.enfoque === 'individual' ? (q.presentMode || undefined) : undefined,
                            text: {
                              es: q.text,
                              // TranslationsSection stores keys as "q_0", "q_1", etc. (index-based, NOT q.id).
                              ...Object.fromEntries(
                                Object.entries(form.translations)
                                  .filter(([lang]) => form.translationsEnabled[lang] !== false)
                                  .flatMap(([lang, map]) => {
                                    const v = map[`q_${idx}`];
                                    return v?.trim() ? [[lang, v]] : [];
                                  })
                              ),
                            },
                          })),
                          campaignWeight: form.campaignWeight,
                          activeDays: form.activeDays,
                          boostUrgente: form.boostUrgente,
                          boostDays: form.boostDays,
                          minRespondents: form.minRespondents,
                          maxRespondents: form.maxRespondents,
                        },
                      }, {
                        onSuccess: () => onClose(),
                        onError: (err) => setPublishError(err.message),
                      });
                    }}
                  >
                    {updateSurvey.isPending
                      ? <><i className="fas fa-spinner fa-spin" /> {t('common.saving')}</>
                      : <><i className="fas fa-save" /> {t('common.save')}</>}
                  </button>
                </>
              ) : source !== 'templates' ? (
                /* Create mode: Publish (surveys only) */
                <button
                  className="bld-btn-next"
                  disabled={createSurvey.isPending}
                  onClick={() => {
                    setPublishError(null);
                    createSurvey.mutate(form, {
                      onSuccess: () => onClose(),
                      onError: (err) => setPublishError(err.message),
                    });
                  }}
                >
                  {createSurvey.isPending
                    ? <><i className="fas fa-spinner fa-spin" /> {t('common.saving')}</>
                    : <><i className="fas fa-rocket" /> {t('admin.builderPublish')}</>}
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {/* Resize handle */}
        {previewVisible && (
          <div
            ref={handleRef}
            className="bld-resize-handle"
            onMouseDown={onHandleMouseDown}
          >
            <div className="bld-resize-grip" />
          </div>
        )}

        {/* Preview panel */}
        {previewVisible ? (
          <div className="bld-preview-panel" style={{ width: previewWidth }}>
            <div className="bld-preview-header">
              <h3><i className="fas fa-eye" /> {t('admin.builderPreview')}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="bld-preview-badge">Live</span>
                <button
                  type="button"
                  className="bld-preview-toggle-btn"
                  onClick={() => setPreviewVisible(false)}
                  title={t('admin.builderHidePreview')}
                >
                  <i className="fas fa-eye-slash" />
                </button>
              </div>
            </div>
            <div className="bld-preview-body">
              <PreviewPanel
                onOpenIconPicker={setPreviewIconTarget}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="bld-preview-show-btn"
            onClick={() => setPreviewVisible(true)}
            title={t('admin.builderShowPreview')}
          >
            <i className="fas fa-eye" />
            <span>{t('admin.builderPreview')}</span>
          </button>
        )}
      </div>

      {/* ── Icon picker for preview questions ── */}
      {previewIconTarget !== null && (
        <IconPickerModal
          currentIcon={form.questions[previewIconTarget]?.icon ?? ''}
          onSelect={v => {
            setForm(f => ({
              ...f,
              questions: f.questions.map((q, i) => i === previewIconTarget ? { ...q, icon: v } : q),
            }));
            setPreviewIconTarget(null);
          }}
          onClose={() => setPreviewIconTarget(null)}
        />
      )}

      {/* ── Validation modal ── */}
      {validationErrors.length > 0 && (
        <div
          className="bcm-overlay"
          onClick={e => { if (e.target === e.currentTarget) clearValidationErrors(); }}
        >
          <div className="bcm-box">
            <div className="bcm-icon bcm-icon--error">
              <i className="fas fa-exclamation-circle" />
            </div>
            <h3 className="bcm-title">{t('admin.builderValidationTitle')}</h3>
            <p className="bcm-desc">{t('admin.builderValidationDesc')}</p>
            <ul className="bcm-error-list">
              {validationErrors.map(err => (
                <li key={err}><i className="fas fa-times-circle" /> {err}</li>
              ))}
            </ul>
            <div className="bcm-actions">
              <button className="bcm-btn bcm-confirm" onClick={clearValidationErrors}>
                {t('admin.builderValidationClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Exit confirmation modal ── */}
      {showExitModal && (
        <div
          className="bcm-overlay"
          onClick={e => { if (e.target === e.currentTarget) handleExitCancel(); }}
        >
          <div className="bcm-box">
            <div className="bcm-icon">
              <i className="fas fa-exclamation-triangle" />
            </div>
            <h3 className="bcm-title">{t('admin.builderExitTitle')}</h3>
            <p className="bcm-desc">{t('admin.builderExitDesc')}</p>
            <div className="bcm-actions">
              <button className="bcm-btn bcm-cancel" onClick={handleExitCancel}>
                {t('admin.builderExitCancel')}
              </button>
              <button className="bcm-btn bcm-confirm" onClick={handleExitConfirm}>
                {t('admin.builderExitConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </BuilderContext.Provider>
  );
}
